import { log } from '@/log'
import {
  CurrentUser,
  CurrentUserScheme,
} from '@/services/api/current-user'
import {
  AggregatedPoint,
  PointService,
} from '@/services/point'
import { LoadSessionData } from '@/services/session/load-session-data'
import { GpPromotionContext } from '@/vendor/gtm/events/gp/promotion-context'
import { Either, left, right } from 'fp-ts/lib/Either'
import { PromotionContextCache } from './cache'

export interface PromotionContextRepository {
  get(): Promise<Either<Error, GpPromotionContext>>
}

export class DefaultPromotionContextRepository implements PromotionContextRepository {
  static createDefault(): DefaultPromotionContextRepository {
    return new DefaultPromotionContextRepository()
  }

  constructor(
    private readonly cache: PromotionContextCache = PromotionContextCache.createDefault(),
    private readonly currentUser: CurrentUser = new CurrentUser(),
    private readonly pointService: PointService = PointService.createDefault(),
    private readonly loadSessionData: LoadSessionData = LoadSessionData.getInstance()
  ) {}

  async get(): Promise<Either<Error, GpPromotionContext>> {
    const cacheUserId = await this.resolveCacheUserId()
    if (cacheUserId !== null) {
      const cached = this.cache.get(cacheUserId)
      if (cached !== null) return right(cached)
    }

    log.debug('Promotion context cache is not found. Fetching promotion context.')

    const [currentUserResult, pointResult] = await Promise.all([
      this.currentUser.getCurrentUser(),
      this.pointService.getAggregated({
        order_key: 'expired_at',
        order_direction: 'asc',
      }),
    ])

    if (currentUserResult.isLeft()) {
      return left(currentUserResult.value)
    }

    if (pointResult.isLeft()) {
      log.error('Failed to get point data for promotion context', pointResult.value)

      return left(pointResult.value)
    }

    const context = buildPromotionContext(currentUserResult.value, pointResult.value)
    this.cache.set(context)

    return right(context)
  }

  private async resolveCacheUserId(): Promise<string | null> {
    try {
      const payload = await this.loadSessionData.load()

      return payload.data.userId
    } catch (error) {
      log.error('Failed to resolve session user id for promotion context cache', error)

      return null
    }
  }
}

export function buildPromotionContext(
  user: CurrentUserScheme,
  points: AggregatedPoint[]
): GpPromotionContext {
  const sortedPoints = [...points].sort(
    (a, b) => Date.parse(a.expired_at) - Date.parse(b.expired_at)
  )
  const nextExpiringPoint = sortedPoints.find(point => point.point > 0)
  const totalPointAmount = points.reduce((total, point) => total + point.point, 0)

  return {
    user: {
      id: user.id,
      short_id: user.shortid,
      status: user.user_status,
    },
    order: {
      order_times: user.order_times || 0,
      last_order_date: user.last_order_date || null,
    },
    point: {
      total_amount: totalPointAmount,
      next_expiring_amount: nextExpiringPoint ? nextExpiringPoint.point : null,
      next_expiring_date: nextExpiringPoint ? nextExpiringPoint.expired_at : null,
    },
  }
}
